import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { shared } from "../styles.js";
import type { VersionRecord, TagRecord } from "../api/registry.js";
import { shortDigest, formatDate } from "../lib/format.js";
import { extractCompat } from "../lib/compat-labels.js";
import "./tag-badge.js";

/**
 * version-list renders the version-history table (R9): version, publish date, short
 * digest, declared compat ranges (registry.compat.* labels), the tags pointing at
 * each version, and deprecated/yanked badges with their messages as a muted second
 * line. Each version links to its frozen Document view.
 */
@customElement("version-list")
export class VersionList extends LitElement {
  static override styles = [
    shared,
    css`
      .msg-line {
        color: var(--text-faint);
        font-size: var(--fs-1);
        max-width: 260px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ];

  @property({ attribute: false }) versions: VersionRecord[] = [];
  @property({ attribute: false }) tags: TagRecord[] = [];
  @property() scope = "";
  @property() name = "";

  private tagsFor(versionId: string): TagRecord[] {
    return this.tags.filter((t) => t.version === versionId);
  }

  /** compatText renders a version's compat ranges compactly ("eng >=1.2 · cli ^0.8"). */
  private compatText(v: VersionRecord): string {
    const labels = ((v.document as { metadata?: { labels?: Record<string, string> } })?.metadata
      ?.labels ?? {}) as Record<string, string>;
    const c = extractCompat(labels);
    const parts: string[] = [];
    if (c.engine) parts.push(`eng ${c.engine}`);
    if (c.cli) parts.push(`cli ${c.cli}`);
    return parts.join(" · ") || "—";
  }

  override render() {
    if (!this.versions.length) {
      return html`<p class="muted">No versions yet.</p>`;
    }
    return html`
      <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Version</th>
            <th>Published</th>
            <th>Digest</th>
            <th>Compat</th>
            <th>Tags</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${this.versions.map(
            (v) => html`
              <tr>
                <td>
                  <a href=${`/p/${this.scope}/${this.name}/v/${v.version}`} class="mono">${v.version}</a>
                </td>
                <td class="muted">${formatDate(v.created)}</td>
                <td class="mono muted" title=${v.digest}>${shortDigest(v.digest)}</td>
                <td class="mono muted">${this.compatText(v)}</td>
                <td>
                  ${this.tagsFor(v.id).map(
                    (t) => html`<tag-badge .name=${t.name} ?managed=${t.managed}></tag-badge>`,
                  )}
                </td>
                <td>
                  ${v.yanked ? html`<span class="badge-yanked">yanked</span>` : ""}
                  ${v.deprecated ? html`<span class="badge-deprecated">deprecated</span>` : ""}
                  ${v.yanked && v.yankedMsg
                    ? html`<div class="msg-line" title=${v.yankedMsg}>${v.yankedMsg}</div>`
                    : ""}
                  ${v.deprecated && v.deprecatedMsg
                    ? html`<div class="msg-line" title=${v.deprecatedMsg}>${v.deprecatedMsg}</div>`
                    : ""}
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "version-list": VersionList;
  }
}
